import { BaseFlexScheduleRequest } from "../../api/work-policy-api.types";
import {
  FlexibleScheduleEditor,
  FlexibleScheduleRow
} from "../work-type-shared/flexible-schedule-editor";
import {
  reconcileDailyDateSchedule,
  WorkPolicyFormErrors,
  WorkPolicyFormValue
} from "./hooks/use-work-policy-form";
import {
  calculateAutomaticBreakMinutes,
  calculateExpectedEndTime
} from "./work-policy.validation";
import { isCalendarDate } from "./calendar-date";
import {
  ActionButton,
  ActionRow,
  ErrorText,
  FlexibleModeCopy,
  FlexibleModeGrid,
  FlexibleModeHeading,
  FlexibleModeOption,
  FlexibleModeStatus,
  FormSection,
  GuidePanel
} from "./work-type-management.styles";

const WEEKDAYS = ["월요일", "화요일", "수요일", "목요일", "금요일", "토요일", "일요일"];

interface FlexibleRuleSectionProps {
  value: WorkPolicyFormValue;
  errors: WorkPolicyFormErrors;
  onChange: <K extends keyof WorkPolicyFormValue>(
    field: K,
    value: WorkPolicyFormValue[K]
  ) => void;
  confirmDetailChange: (message: string) => boolean;
  readOnly?: boolean;
}

function scheduleRows(value: WorkPolicyFormValue): FlexibleScheduleRow[] {
  return value.schedule.map((row, index) => ({
    id: row.workDate ?? `weekday-${row.weekdayNo ?? index + 1}`,
    label:
      value.flexibleMode === "DAILY_DATE"
        ? row.workDate || `${index + 1}번 날짜`
        : WEEKDAYS[(row.weekdayNo ?? index + 1) - 1] || `${index + 1}번 요일`,
    startTime: row.startTime ?? "",
    workMinutes: row.plannedWorkMinutes,
    weekday: row.weekdayNo === null ? undefined : row.weekdayNo % 7,
    isDayOff: !row.workday,
    waiveBreakAtFourHours: row.breakWaiver,
    selected: value.scheduleSelectedKeys.includes(
      row.workDate ?? `weekday-${row.weekdayNo ?? index + 1}`
    )
  }));
}

function scheduleTargetMinutes(value: WorkPolicyFormValue) {
  const baseHours = Number(
    value.flexibleMode === "DAILY_DATE"
      ? value.dailyBaseHours
      : value.weeklyBaseHours
  );
  if (!Number.isFinite(baseHours) || baseHours <= 0) return null;

  if (value.flexibleMode === "WEEKLY_PATTERN") {
    return Math.round(baseHours * 60);
  }

  if (
    !isCalendarDate(value.effectiveStartDate) ||
    !isCalendarDate(value.effectiveEndDate) ||
    value.effectiveStartDate > value.effectiveEndDate
  ) {
    return null;
  }

  const editableWorkdayCount = value.schedule.filter(row => row.workday).length;
  return editableWorkdayCount > 0
    ? Math.round(baseHours * 60 * editableWorkdayCount)
    : null;
}

function defaultSchedule(value: WorkPolicyFormValue, mode: WorkPolicyFormValue["flexibleMode"]) {
  if (mode === "WEEKLY_PATTERN") {
    return WEEKDAYS.map<BaseFlexScheduleRequest>((_, index) => ({
      workDate: null,
      weekdayNo: index + 1,
      startTime: index < 5 ? "09:00" : null,
      plannedWorkMinutes: index < 5 ? 480 : 0,
      breakWaiver: false,
      workday: index < 5
    }));
  }
  return reconcileDailyDateSchedule(
    value.effectiveStartDate,
    value.effectiveEndDate,
    []
  );
}

export const FlexibleRuleSection = ({
  value,
  errors,
  onChange,
  confirmDetailChange,
  readOnly = false
}: FlexibleRuleSectionProps) => {
  const changeMode = (mode: WorkPolicyFormValue["flexibleMode"]) => {
    if (mode === value.flexibleMode) return;
    if (
      value.schedule.length > 0 &&
      !confirmDetailChange("탄력 일정 방식을 바꾸면 현재 일정 행이 초기화됩니다. 계속할까요?")
    ) {
      return;
    }
    onChange("flexibleMode", mode);
    onChange("scheduleSelectedKeys", []);
    onChange("schedule", value.policyKind === "BASE" ? defaultSchedule(value, mode) : []);
  };

  return (
    <FormSection id="work-policy-schedule">
      <legend>탄력적 근로시간제 추가 설정</legend>
      <p>{value.policyKind === "APPROVAL" ? "신청자가 결재 신청에서 실제 근무 일정을 입력합니다." : "적용기간 안의 근무 일정을 날짜별 설정 또는 주간 패턴으로 관리합니다."}</p>
      <FlexibleModeHeading>
        <strong id="flexible-mode-heading">스케줄 구성 방식</strong>
        <FlexibleModeStatus>정책별 1개 방식 지정</FlexibleModeStatus>
      </FlexibleModeHeading>
      <FlexibleModeGrid
        role="radiogroup"
        aria-labelledby="flexible-mode-heading"
      >
        <FlexibleModeOption $checked={value.flexibleMode === "DAILY_DATE"}>
          <input
            type="radio"
            name="flexibleMode"
            checked={value.flexibleMode === "DAILY_DATE"}
            disabled={readOnly}
            onChange={() => changeMode("DAILY_DATE")}
          />
          <span aria-hidden="true">▣</span>
          <FlexibleModeCopy>
            <strong>날짜별 설정</strong>
            <small>
              적용기간의 실제 날짜마다 출근시간과 계획근로시간을 설정합니다.
            </small>
          </FlexibleModeCopy>
        </FlexibleModeOption>
        <FlexibleModeOption $checked={value.flexibleMode === "WEEKLY_PATTERN"}>
          <input
            type="radio"
            name="flexibleMode"
            checked={value.flexibleMode === "WEEKLY_PATTERN"}
            disabled={readOnly}
            onChange={() => changeMode("WEEKLY_PATTERN")}
          />
          <span aria-hidden="true">↻</span>
          <FlexibleModeCopy>
            <strong>주간 패턴</strong>
            <small>월~일 패턴을 설정하고 적용기간 동안 반복합니다.</small>
          </FlexibleModeCopy>
        </FlexibleModeOption>
      </FlexibleModeGrid>
      <GuidePanel aria-label="탄력 일정 신청 안내">
        <h3>결재 신청에는 선택한 방식이 자동 적용됩니다.</h3>
        <p>
          날짜별 설정이면 신청 From~To의 실제 날짜 스케줄을, 주간 패턴이면
          월~일 패턴을 신청자가 상세 일정에서 입력합니다.
        </p>
        <p>
          하나의 탄력 정책에는 날짜별 설정 또는 주간 패턴 중 하나만 지정합니다.
          휴게시간과 예상 퇴근은 근로시간 기준에 따라 자동 계산됩니다.
        </p>
        <p>세부 생성·통계 규칙은 아래 적용·생성·통계 기준에서 확인하세요.</p>
      </GuidePanel>
      {value.policyKind === "APPROVAL" ? (
        <p>여기서는 일정 방식만 정하고 실제 일정은 결재 신청에서 입력합니다.</p>
      ) : (
        <>
          {value.schedule.length === 0 ? (
            <ActionRow>
              {!readOnly && value.flexibleMode === "WEEKLY_PATTERN" ? (
                <ActionButton
                  type="button"
                  onClick={() => onChange("schedule", defaultSchedule(value, value.flexibleMode))}
                >
                  주간 일정 만들기
                </ActionButton>
              ) : (
                <span>적용 시작일과 종료일을 입력하면 날짜별 일정이 자동 생성됩니다.</span>
              )}
            </ActionRow>
          ) : (
            <>
              <FlexibleScheduleEditor
                mode={value.flexibleMode}
                presentation="productivity"
                targetMinutes={scheduleTargetMinutes(value)}
                sectionGuide={
                  value.flexibleMode === "DAILY_DATE"
                    ? "적용기간의 날짜를 선택하고 목표시간에 맞게 일괄 조정하세요."
                    : "월~일 반복 패턴을 선택하고 주간 목표시간에 맞게 일괄 조정하세요."
                }
                value={scheduleRows(value)}
                getRowPreview={row => {
                  if (row.isDayOff) return null;
                  const automaticBreak = calculateAutomaticBreakMinutes(
                    row.workMinutes,
                    value.breakCalculationEnabled,
                    Number(value.breakWorkMinutes),
                    Number(value.breakMinutes),
                    row.waiveBreakAtFourHours
                  );
                  return {
                    automaticBreakMinutes: automaticBreak,
                    expectedEnd: calculateExpectedEndTime(
                      row.startTime,
                      row.workMinutes,
                      automaticBreak
                    )
                  };
                }}
                onChange={rows => {
                  onChange(
                    "scheduleSelectedKeys",
                    rows.filter(row => row.selected).map(row => row.id)
                  );
                  onChange(
                    "schedule",
                    rows.map((row, index) => {
                      const source = value.schedule[index];
                      return {
                        workDate:
                          value.flexibleMode === "DAILY_DATE"
                            ? source.workDate
                            : null,
                        weekdayNo:
                          value.flexibleMode === "WEEKLY_PATTERN"
                            ? source.weekdayNo
                            : null,
                        startTime: row.isDayOff ? null : row.startTime,
                        plannedWorkMinutes: row.isDayOff ? 0 : row.workMinutes,
                        breakWaiver: row.isDayOff
                          ? false
                          : row.waiveBreakAtFourHours,
                        workday: !row.isDayOff
                      };
                    })
                  );
                }}
                readOnly={readOnly}
              />
            </>
          )}
        </>
      )}
      {errors.schedule && <ErrorText>{errors.schedule}</ErrorText>}
    </FormSection>
  );
};
